<template>
  <div>
    <div class="mb-4 flex justify-content-between align-items-center">
      <span class="pr-2 mr-2 font-bold text-2xl"
        >Rekonsiliasi Faktur Penjualan dengan FP Keluaran</span
      >
      <div>
        <i class="pi pi-home mr-2"></i>
        <span class="font-small"
          >/ Pajak / Rekonsiliasi Faktur Penjualan dengan FP Keluaran
        </span>
      </div>
    </div>

    <div class="card bg-white">
      <div class="flex justify-content-end mb-2">
        <div class="flex align-items-center">
          <input
            ref="fileInput"
            type="file"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            class="hidden"
            @change="handleFileSelect"
          />
          <Button
            :label="fileButtonLabel"
            :icon="fileButtonIcon"
            class="p-button mr-2"
            :loading="isLoadingImport"
            @click="onFileButtonClick"
          />
        </div>
        
        <Button
          :loading="isLoadingExport"
          icon="pi pi-file-excel"
          label="Export"
          @click="onExport()"
        />
      </div>
      <grid-rekonsiliasi-penjualan
        :items="items"
        :loading="isLoading"
        :total="totalRecords"
        :grid-options="options"
        @request="onRequestData"
      />
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import RekonsiliasiPenjualanService from '@/services/RekonsiliasiPenjualanService'
import GridRekonsiliasiPenjualan from '@/components/pajak/GridRekonsiliasiPenjualan'
import errorHandler from '@/helpers/error-handler'

export default {
  setup: () => ({ v$: useVuelidate() }),
  components: {
    GridRekonsiliasiPenjualan,
  },
  data() {
    return {
      dataService: null,
      isLoading: false,
      isLoadingImport: false,
      isLoadingExport: false,
      items: [],
      invoices: [],
      selects: null,
      totalRecords: 0,
      options: {
        page: 1,
        rows: 10,
        sortField: '-si_at',
        sortOrder: -1,
        filters: null,
      },
      submitted: false,
      form: {
        id: 0,
        date_from: null,
        date_to: null,
      },
      selectedFile: null,
    }
  },
  created() {
    this.dataService = new RekonsiliasiPenjualanService()
  },
  mounted() {
    this.onLoadGridData()
  },
  computed: {
    fileButtonLabel() {
      return this.selectedFile ? ` ${this.selectedFile.name}` : 'Import';
    },
    fileButtonIcon() {
      return this.selectedFile ? 'pi pi-upload' : 'pi pi-paperclip';
    },
  },
  methods: {
    onRequestData(e) {
      this.options = e
      this.onLoadGridData()
    },
    onLoadGridData() {
      const page = this.options.page
      const perPage = this.options.rows
      const sortField = this.options.sortField
      const sortOrder = this.options.sortOrder
      let filters = this.options.filters
      this.isLoading = true
      this.dataService
        .paginate(page, perPage, sortField, sortOrder, filters)
        .then((res) => {
          this.items = res.data.data
          this.totalRecords = res.data.total
          this.isLoading = false
        })
        .catch((err) => {
          errorHandler(err, 'Rekonsiliasi Masukan', this)
        })
        .finally(() => (this.isLoading = false))
    },
    onExport() {
      const sortField = this.options.sortField
      const sortOrder = this.options.sortOrder
      const filters = this.options.filters
      this.isLoadingExport = true
      this.dataService
        .export(sortField, sortOrder, filters)
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', 'rekonsiliasi-keluaran.xls')
          document.body.appendChild(link)
          link.click()
        })
        .catch((err) => {
          errorHandler(err, 'Export Data Rekonsiliasi Keluaran', this)
        })
        .finally(() => (this.isLoadingExport = false))
    },
    resetForm() {
      this.submitted = false
      this.form.date_to = null
      this.form.date_from = null
    },
    onFileButtonClick() {
      if (this.selectedFile) {
        this.importFile();
      } else {
        this.$refs.fileInput.click();
      }
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      const MAX_SIZE = 10000000; // 10 MB

      if (file) {
        if (file.size > MAX_SIZE) {
          this.$toast.add({
            severity: 'warn',
            summary: 'Ukuran File Terlalu Besar',
            detail: 'Ukuran maksimal file adalah 10 MB.',
            life: 3000,
          });
          this.selectedFile = null;
          this.$refs.fileInput.value = null;
          return;
        }

        this.selectedFile = file;
      }
    },
    importFile() {
      if (!this.selectedFile) return;

      this.isLoadingImport = true;

      this.dataService
        .import(this.selectedFile)
        .then((res) => {
          if (res.data.status === 200) {
            this.$toast.add({
              severity: 'success',
              summary: 'Import File Rekonsiliasi Penjualan',
              detail: 'Berhasil import Rekonsiliasi Penjualan.',
              life: 3000,
            });
            this.onLoadGridData();
            this.selectedFile = null;
          } else {
            let msg = [];
            res.data.data.forEach((value) => msg.push(value.error));
            this.$toast.add({
              severity: 'error',
              summary: `${res.data.message}`,
              detail: `${msg}`,
              life: 4000,
            });
            this.selectedFile = null;
          }
        })
        .catch((err) => {
          errorHandler(err, 'Import File Rekonsiliasi Penjualan', this);
          this.selectedFile = null;
        })
        .finally(() => {
          this.isLoadingImport = false;
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = null;
          }
        });
    }
  },
  validations() {
    return {
      form: {
        date_from: {
          required: helpers.withMessage(
            'Tanggal (dari) harus diisi.',
            required
          ),
        },
        date_to: {
          required: helpers.withMessage('Tanggal (s/d) harus diisi.', required),
        },
      },
    }
  },
}
</script>
